// invite-flow Wave-3B: web auth-flow coordinator hook.
//
// The hook is a thin host: it consumes the shared
// `authFlowReducer` + `initialAuthFlowMachine` from `@ping/ui`
// and wires them to web's BE clients + IndexedDB-backed identity /
// token persistence. The shared step screens (also in `@ping/ui`)
// emit events the coordinator handles via these effects.
//
// Hard rule 3: this hook never logs the user's email / phone / code
// / password / displayName. Only verb-shaped `event` discriminants
// + short integer metadata are passed to `log.*`.

import { useCallback, useMemo, useReducer } from 'react';

import {
  type AuthenticatedUser as AuthenticatedUser,
  type AuthFailureReason as AuthFailureReason,
  type AuthFlowAction as AuthFlowAction,
  type AuthFlowMachine as AuthFlowMachine,
  authFlowReducer,
  type ContactValue as ContactValue,
  initialAuthFlowMachine,
  pingToast,
} from '@ping/ui';

import { log } from '@core/log/log';
import {
  NetworkError,
  requestEmailCode,
  requestSmsCode,
  verifyEmailCode,
  verifySmsCode,
} from '@core/network';
import { patch } from '@core/network/api';
import { bytesToHex, getDevicePublicKeyBytes, getOrCreateDeviceId } from '@core/network/deviceKeys';
import { setAccessToken } from '@core/network/interceptor';
import { retrieveIdentity, storeIdentity } from '@core/persistence/identity';
import { storeWrappedRefreshToken } from '@core/persistence/tokens';

// `verifiedIdentity` captures what `verifySmsCode` /
// `verifyEmailCode` returned so the finalize step (sign-up only) can
// merge it into the final `AuthenticatedUser` handoff. Held in a
// closure-scoped ref-like object — never persisted to module scope.
export interface VerifiedIdentity {
  readonly accountId: string;
  readonly deviceId: string;
  readonly displayName: string;
  /**
   * Server-authoritative gate: when true, the post-bootstrap host
   * MUST drive the user through the recovery-key screen and the
   * `POST /v1/recovery/blobs` upload before any other endpoint
   * unblocks (the API enforces this via `RequireBackedUp`). True
   * on every fresh signup; true for legacy accounts only if the
   * server-side backfill hasn't run; false for every returning
   * sign-in.
   */
  readonly recoveryBackupRequired: boolean;
}

export interface AuthFlowEffects {
  requestEmail(email: string): Promise<string | null>;
  requestPhone(e164: string): Promise<string | null>;
  verifyCode(
    code: string,
    contact: ContactValue,
    challengeId: string,
    displayName: string | null,
  ): Promise<VerifiedIdentity | null>;
  finalizeRegister(
    verified: VerifiedIdentity | null,
    displayName: string,
  ): Promise<AuthenticatedUser>;
}

export interface UseAuthFlowCoordinatorResult {
  machine: AuthFlowMachine;
  dispatch: (action: AuthFlowAction) => void;
  effects: AuthFlowEffects;
}

function mapVerifyError(err: unknown): AuthFailureReason {
  if (err instanceof NetworkError) {
    // The BE distinguishes "wrong OTP" from "identity_mismatch" via
    // the body's `error` field, not the status code (both are 403).
    // Without this branch the user sees "Code didn't match" when the
    // real problem is "this account is bound to a different device
    // identity" — which has a totally different fix.
    if (err.serverError === 'identity_mismatch') {
      return 'identityMismatch';
    }
    // Sign-IN to a 3PID with no account. The OTP was correct, but there
    // is nothing to sign into — keep the user in sign-in and point them
    // at sign-up rather than silently onboarding them.
    if (err.serverError === 'account_not_found') {
      return 'accountNotFound';
    }
    // A lockout after too many wrong codes is NOT "wrong code" — surface the
    // cooldown so the user waits instead of frantically re-entering (which
    // can't succeed and deepens the lockout). The BE returns 403/429
    // `account_locked` (and 429 `rate_limited`); map both to the cooldown copy.
    if (err.serverError === 'account_locked' || err.serverError === 'rate_limited') {
      return 'rateLimited';
    }
    // `challenge_invalid` (400) — the OTP challenge is gone/expired. With the
    // backend fix (a WRONG code no longer consumes the challenge) this only
    // happens on a genuinely expired challenge, not on a wrong-then-right
    // retry. Keep the user on the verify screen to re-enter / resend rather
    // than showing the misleading "Couldn't reach the server" (the old 400 →
    // 'network' fallthrough).
    if (err.serverError === 'challenge_invalid') {
      return 'codeMismatch';
    }
    if (err.statusCode === 429) {
      return 'rateLimited';
    }
    if (err.statusCode === 401 || err.statusCode === 403) {
      return 'codeMismatch';
    }
    if (err.statusCode !== undefined && err.statusCode >= 500) {
      return 'serverError';
    }
    return 'network';
  }
  return 'network';
}

// User-facing copy for every `AuthFailureReason`. Keeps the toast
// strings centralised so we can localise / tone-tune them in one place.
function describeFailure(reason: AuthFailureReason): {
  title: string;
  description?: string;
} {
  switch (reason) {
    case 'network':
      return {
        title: "Couldn't reach the server",
        description: 'Check your connection and try again.',
      };
    case 'codeMismatch':
      return {
        title: 'Wrong code',
        description: 'Double-check the 6-digit code we sent and try again.',
      };
    case 'identityMismatch':
      return {
        title: 'You already have an active session/device',
        description:
          'Sign in through QR linking if that device is reachable, or use the recovery flow if it is unreachable.',
      };
    case 'accountNotFound':
      return {
        title: 'Account not found',
        description: "We couldn't find an account for that email or phone. Tap “Sign up” to create one.",
      };
    case 'invalidCredential':
      return {
        title: 'Sign-in expired',
        description: 'Please sign in again.',
      };
    case 'passwordTooWeak':
      return { title: 'Pick a stronger password' };
    case 'phoneInvalid':
      return { title: 'That phone number looks off' };
    case 'emailInvalid':
      return { title: 'That email looks off' };
    case 'rateLimited':
      return {
        title: 'Too many attempts',
        description: 'Give it a minute and try again.',
      };
    case 'serverError':
      return {
        title: 'Something went wrong',
        description: "We're on it — please try again shortly.",
      };
    default: {
      const _exhaustive: never = reason;
      void _exhaustive;
      return { title: "Something didn't work" };
    }
  }
}

function toastForFailure(reason: AuthFailureReason): void {
  const { title, description } = describeFailure(reason);
  pingToast.error(description ? { title, description } : { title });
}

function mapRequestError(err: unknown): AuthFailureReason {
  if (err instanceof NetworkError) {
    if (err.statusCode === 429) {
      return 'rateLimited';
    }
    if (err.statusCode !== undefined && err.statusCode >= 500) {
      return 'serverError';
    }
  }
  return 'network';
}

export function useAuthFlowCoordinator(): UseAuthFlowCoordinatorResult {
  const [machine, dispatch] = useReducer(loggingReducer, initialAuthFlowMachine);

  const requestEmail = useCallback(async (email: string): Promise<string | null> => {
    log.info({ event: 'auth.email.submitted' });
    try {
      const { challengeId } = await requestEmailCode({ email });
      return challengeId;
    } catch (err) {
      log.warn({ event: 'auth.email.requestFailed' });
      const reason = mapRequestError(err);
      // User feedback via toast; we deliberately do NOT dispatch
      // `{type:'failed'}` here. Keeping the state machine on its
      // current step lets the user fix their input inline and retry
      // without re-traversing the whole flow.
      toastForFailure(reason);
      return null;
    }
  }, []);

  const requestPhone = useCallback(async (e164: string): Promise<string | null> => {
    log.info({ event: 'auth.phone.submitted' });
    try {
      const { challengeId } = await requestSmsCode({ phoneE164: e164 });
      return challengeId;
    } catch (err) {
      log.warn({ event: 'auth.phone.requestFailed' });
      const reason = mapRequestError(err);
      // User feedback via toast; we deliberately do NOT dispatch
      // `{type:'failed'}` here. Keeping the state machine on its
      // current step lets the user fix their input inline and retry
      // without re-traversing the whole flow.
      toastForFailure(reason);
      return null;
    }
  }, []);

  const verifyCode = useCallback(
    async (
      code: string,
      contact: ContactValue,
      challengeId: string,
      displayName: string | null,
    ): Promise<VerifiedIdentity | null> => {
      try {
        // device_id alignment is now done at SDK init time (the SDK
        // adopts the auth-layer device signing key via
        // `ClientConfig.deviceSigningSecretKey`), so the legacy
        // SHA-256(pubkey) device_id we compute here matches what
        // the SDK will derive on bootstrap — no separate
        // `sdkDeviceIdBytes` field needed at verify time.
        const [devicePublicKeyBytes, deviceId] = await Promise.all([
          getDevicePublicKeyBytes(),
          getOrCreateDeviceId(),
        ]);
        const verifyArgs = {
          challengeId,
          code,
          deviceId,
          signaturePublicKey: bytesToHex(devicePublicKeyBytes),
          displayName: displayName ?? undefined,
          // Tell the BE whether this is a sign-IN or sign-UP. On
          // 'signin' the server refuses to create a missing account
          // and returns account_not_found (mapped below) — so a
          // mistyped/never-registered 3PID can't silently onboard a
          // user who meant to log in.
          intent: (machine.context.authMode === 'register' ? 'signup' : 'signin') as
            | 'signin'
            | 'signup',
        };
        const result =
          contact.kind === 'email'
            ? await verifyEmailCode(verifyArgs)
            : await verifySmsCode(verifyArgs);

        // displayName precedence:
        //   1. server-returned `result.displayName` (returning user
        //      via the BE profile row),
        //   2. machine context displayName (sign-up pre-collected),
        //   3. empty-string fallback. The sign-up post-verify chain
        //      (displayName + finalize step) will overwrite via
        //      PATCH /v1/me/profile + storeIdentity.
        const resolvedDisplayName =
          (result.displayName && result.displayName.length > 0
            ? result.displayName
            : displayName) ?? '';

        setAccessToken(result.accessToken, result.expiresIn);
        await Promise.all([
          storeWrappedRefreshToken(result.refreshToken),
          storeIdentity({
            accountId: result.accountId,
            deviceId: result.deviceId,
            displayName: resolvedDisplayName,
          }),
        ]);

        return {
          accountId: result.accountId,
          deviceId: result.deviceId,
          displayName: resolvedDisplayName,
          recoveryBackupRequired: result.recoveryBackupRequired ?? false,
        };
      } catch (err) {
        log.warn({ event: 'auth.code.verifyFailed' });
        const reason = mapVerifyError(err);
        // Stay on the OTP step + toast the cause. The user can re-
        // type / re-paste the code without restarting the flow.
        toastForFailure(reason);
        return null;
      }
    },
    [machine.context.authMode],
  );

  const finalizeRegister = useCallback(
    async (verified: VerifiedIdentity | null, displayName: string): Promise<AuthenticatedUser> => {
      const trimmedName = displayName.trim();
      // Persist the freshly-chosen display name to the BE profile row
      // before handing off. Non-fatal: if the BE rejects, log and
      // proceed — the local identity record still captures it via
      // storeIdentity below, and a follow-up Settings → Profile edit
      // can retry.
      if (trimmedName.length > 0) {
        try {
          await patch<unknown>('/v1/me/profile', { display_name: trimmedName });
          log.info({ event: 'auth.resolve.profileUpdated' });
        } catch (err) {
          log.warn({
            event: 'auth.resolve.profileUpdate.failed',
            message: (err as Error).message,
          });
        }
      }

      // Resolve the accountId for the AuthenticatedUser handoff,
      // falling back to the previously-persisted Dexie row when the
      // in-memory `verified` value is missing (e.g. a back-and-forth
      // through the FSM that discarded the post-verify result).
      let accountId = verified?.accountId ?? '';
      let deviceId = verified?.deviceId ?? '';
      if (accountId.length === 0 || deviceId.length === 0) {
        const persisted = await retrieveIdentity();
        if (persisted) {
          accountId = accountId || persisted.accountId;
          deviceId = deviceId || persisted.deviceId;
        }
      }

      // Merge the trimmed display name into the persisted identity so
      // session bootstrap's storeIdentity merge picks up the freshest
      // value.
      if (accountId.length > 0 && deviceId.length > 0 && trimmedName.length > 0) {
        try {
          await storeIdentity({ accountId, deviceId, displayName: trimmedName });
        } catch (err) {
          log.warn({
            event: 'auth.resolve.identityWriteFailed',
            message: (err as Error).message,
          });
        }
      }

      return {
        tokens: { hasAccessToken: true },
        identity: {
          accountId,
          deviceId,
          displayName: trimmedName,
        },
      };
    },
    [],
  );

  const effects = useMemo<AuthFlowEffects>(
    () => ({ requestEmail, requestPhone, verifyCode, finalizeRegister }),
    [requestEmail, requestPhone, verifyCode, finalizeRegister],
  );

  return { machine, dispatch, effects };
}

// loggingReducer wraps the pure reducer with a single safe-logging
// trace per transition. Only `event` + `from` + `to` + (action `type`)
// are emitted — never the action's email / phone / password / etc.
function loggingReducer(machine: AuthFlowMachine, action: AuthFlowAction): AuthFlowMachine {
  const next = authFlowReducer(machine, action);
  if (next.state.kind !== machine.state.kind) {
    log.info({
      event: 'auth.flow.advance',
      from: machine.state.kind,
      to: next.state.kind,
      action: action.type,
    });
  }
  return next;
}
